import { useState, useEffect } from 'react';
import { Icon } from '../../utils/icons';

const AppointmentServices = ({ config, onConfigChange, onShowImageCropper }) => {
    const [showAddServicePanel, setShowAddServicePanel] = useState(false);
    const [editingServiceId, setEditingServiceId] = useState(null);
    const [serviceForm, setServiceForm] = useState({
        id: '',
        name: '',
        description: '',
        image: '',
        imageUrl: '',
        duration: '30',
        durationUnit: 'minutes',
        customDuration: '',
        price: '',
        currency: 'USD'
    });

    const services = config.services || [];

    // Sync service form when services are updated (e.g., after image upload)
    useEffect(() => {
        if (editingServiceId) {
            const updatedService = services.find(service => service.id === editingServiceId);
            if (updatedService && (updatedService.image !== serviceForm.image || updatedService.imageUrl !== serviceForm.imageUrl)) {
                console.log('🔄 Syncing service form with updated service data:', updatedService);
                setServiceForm(prev => ({
                    ...prev,
                    image: updatedService.image || '',
                    imageUrl: updatedService.imageUrl || ''
                }));
            }
        }
    }, [services, editingServiceId, serviceForm.image, serviceForm.imageUrl]);

    // Duration options
    // Duration options
    const durationOptions = [
        { value: '5', label: '5 minutes', unit: 'minutes' },
        { value: '10', label: '10 minutes', unit: 'minutes' },
        { value: '15', label: '15 minutes', unit: 'minutes' },
        { value: '30', label: '30 minutes', unit: 'minutes' },
        { value: '60', label: '1 hour', unit: 'minutes' },
        { value: '120', label: '2 hours', unit: 'minutes' },
        { value: '180', label: '3 hours', unit: 'minutes' },
        { value: '240', label: '4 hours', unit: 'minutes' },
        { value: 'custom', label: 'Custom', unit: 'custom' }
    ];

    // Currency options
    const currencyOptions = [
        { value: 'USD', label: 'USD ($)', symbol: '$' },
        { value: 'EUR', label: 'EUR (€)', symbol: '€' },
        { value: 'GBP', label: 'GBP (£)', symbol: '£' },
        { value: 'CAD', label: 'CAD (C$)', symbol: 'C$' },
        { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
        { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
        { value: 'CHF', label: 'CHF (₣)', symbol: '₣' },
        { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
        { value: 'INR', label: 'INR (₹)', symbol: '₹' },
        { value: 'KRW', label: 'KRW (₩)', symbol: '₩' },
        { value: 'BRL', label: 'BRL (R$)', symbol: 'R$' },
        { value: 'MXN', label: 'MXN ($)', symbol: '$' },
        { value: 'SGD', label: 'SGD (S$)', symbol: 'S$' },
        { value: 'HKD', label: 'HKD (HK$)', symbol: 'HK$' },
        { value: 'NZD', label: 'NZD (NZ$)', symbol: 'NZ$' }
    ];

    const resetServiceForm = () => {
        setServiceForm({
            id: '',
            name: '',
            description: '',
            image: '',
            imageUrl: '',
            duration: '30',
            durationUnit: 'minutes',
            customDuration: '',
            price: '',
            currency: 'USD'
        });
        setEditingServiceId(null);
    };

    const handleAddService = () => {
        resetServiceForm();
        setServiceForm(prev => ({ ...prev, id: Date.now().toString() }));
        setShowAddServicePanel(true);
    };

    const handleEditService = (service) => {
        console.log('📝 Editing service:', service);
        setServiceForm({
            id: service.id,
            name: service.name || '',
            description: service.description || '',
            image: service.image || '',
            imageUrl: service.imageUrl || '',
            duration: service.duration || '30',
            durationUnit: service.durationUnit || 'minutes',
            customDuration: service.customDuration || '',
            price: service.price || '',
            currency: service.currency || 'USD'
        });
        setEditingServiceId(service.id);
        setShowAddServicePanel(true);
    };

    const handleDeleteService = (serviceId) => {
        if (window.confirm('Are you sure you want to delete this service?')) {
            const updatedServices = services.filter(service => service.id !== serviceId);
            onConfigChange({ services: updatedServices });
        }
    };

    const handleSaveService = () => {
        if (!serviceForm.name.trim()) {
            alert('Please enter a service name');
            return;
        }

        let finalDuration = serviceForm.duration;
        let finalDurationUnit = serviceForm.durationUnit;

        if (serviceForm.duration === 'custom') {
            if (!serviceForm.customDuration.trim()) {
                alert('Please enter a custom duration');
                return;
            }
            finalDuration = serviceForm.customDuration;
            finalDurationUnit = 'minutes'; // Default unit for custom duration
        }

        const serviceData = {
            id: serviceForm.id,
            name: serviceForm.name.trim(),
            description: serviceForm.description.trim(),
            image: serviceForm.image,
            imageUrl: serviceForm.imageUrl.trim(),
            duration: finalDuration,
            durationUnit: finalDurationUnit,
            customDuration: serviceForm.duration === 'custom' ? serviceForm.customDuration : '',
            price: serviceForm.price.trim(),
            currency: serviceForm.currency
        };

        let updatedServices;
        if (editingServiceId) {
            // Edit existing service
            updatedServices = services.map(service =>
                service.id === editingServiceId ? serviceData : service
            );
        } else {
            // Add new service
            updatedServices = [...services, serviceData];
        }

        onConfigChange({ services: updatedServices });
        setShowAddServicePanel(false);
        resetServiceForm();
    };

    const handleUploadImage = () => {
        onShowImageCropper({
            type: 'service',
            serviceId: serviceForm.id,
            title: 'Upload Service Image',
            description: 'Upload an image for your service. The image will be optimized and stored securely.',
            aspectRatio: 1.5 // 3:2 aspect ratio for service images
        });
    };

    const formatDuration = (duration, unit, customDuration) => {
        if (duration === 'custom' && customDuration) {
            return `${customDuration} minutes`;
        }

        const durationNum = parseInt(duration);
        if (durationNum >= 60) {
            const hours = Math.floor(durationNum / 60);
            const minutes = durationNum % 60;
            if (minutes === 0) {
                return `${hours} hour${hours > 1 ? 's' : ''}`;
            } else {
                return `${hours}h ${minutes}m`;
            }
        } else {
            return `${duration} minutes`;
        }
    };

    return (
        <div className="p-4 relative h-full">
            {/* Main Services View */}
            <div className={`transition-transform duration-300 ease-in-out ${showAddServicePanel ? '-translate-x-full' : 'translate-x-0'}`}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold">Services</h3>
                        <p className="text-sm text-gray-400 mt-1">
                            Manage the services customers can book appointments for
                        </p>
                    </div>
                </div>

                {/* Widget Title */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                        Widget Title
                    </label>
                    <input
                        type="text"
                        placeholder="Book Your Appointment"
                        value={config.widgetTitle || ''}
                        onChange={(e) => onConfigChange({
                            widgetTitle: e.target.value
                        })}
                        className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">This title will be displayed at the top of your booking widget</p>
                </div>

                {/* Service Display - List View Only */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-3">
                        Service Display
                    </label>
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-blue-600 text-white shadow-md">
                            <Icon name="list" size={16} />
                            <span className="text-sm font-medium">List View</span>
                        </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                        Services are displayed in a clean vertical list with load more functionality
                    </p>
                </div>

                {/* Services List */}
                <div className="space-y-4 mb-6">
                    {services.map((service) => (
                        <div key={service.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                            <div className="flex items-start space-x-4">
                                {/* Service Image */}
                                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-700 flex-shrink-0">
                                    {service.image || service.imageUrl ? (
                                        <img
                                            src={service.image || service.imageUrl}
                                            alt={service.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Icon name="briefcase" size={20} className="text-gray-400" />
                                        </div>
                                    )}
                                </div>

                                {/* Service Details */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                            <h4 className="font-medium text-white text-sm truncate">{service.name}</h4>
                                            {service.description && (
                                                <p className="text-xs text-gray-400 mt-1 line-clamp-2">{service.description}</p>
                                            )}
                                            <div className="flex items-center space-x-3 mt-2 text-xs text-gray-300">
                                                {service.price && (
                                                    <span className="flex items-center space-x-1">
                                                        <Icon name="dollar" size={12} />
                                                        <span>
                                                            {currencyOptions.find(c => c.value === (service.currency || 'USD'))?.symbol || '$'}
                                                            {service.price}
                                                        </span>
                                                    </span>
                                                )}
                                                <span className="flex items-center space-x-1">
                                                    <Icon name="clock" size={12} />
                                                    <span>{formatDuration(service.duration, service.durationUnit, service.customDuration)}</span>
                                                </span>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center space-x-2 ml-4">
                                            <button
                                                onClick={() => handleEditService(service)}
                                                className="text-gray-400 hover:text-blue-400 transition-colors p-1"
                                                title="Edit service"
                                            >
                                                <Icon name="edit" size={16} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteService(service.id)}
                                                className="text-gray-400 hover:text-red-400 transition-colors p-1"
                                                title="Delete service"
                                            >
                                                <Icon name="delete" size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    {services.length === 0 && (
                        <div className="text-center py-8 text-gray-400">
                            <Icon name="briefcase" size={48} className="mx-auto mb-4 opacity-50" />
                            <p className="text-sm">No services added yet</p>
                            <p className="text-xs mt-1">Add your first service to get started</p>
                        </div>
                    )}
                </div>

                {/* Add Service Button */}
                <button
                    onClick={handleAddService}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                    <Icon name="add" size={18} />
                    <span>Add Service</span>
                </button>
            </div>

            {/* Service Form Slider */}
            <div className={`absolute inset-0 bg-gray-900 transition-transform duration-300 ease-in-out ${showAddServicePanel ? 'translate-x-0' : 'translate-x-full'} flex flex-col h-full`}>
                {/* Form Header */}
                <div className="flex items-center space-x-3 p-4 border-b border-gray-700">
                    <button
                        onClick={() => {
                            setShowAddServicePanel(false);
                            resetServiceForm();
                        }}
                        className="text-gray-400 hover:text-gray-300 transition-colors p-1 rounded-lg hover:bg-gray-800"
                    >
                        <Icon name="back" size={20} />
                    </button>
                    <div>
                        <h3 className="text-lg font-semibold text-white">
                            {editingServiceId ? 'Edit Service' : 'Add Service'}
                        </h3>
                        <p className="text-sm text-gray-400">
                            Configure your service details
                        </p>
                    </div>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-4" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                    <div className="space-y-6">
                        {/* Service Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Service Name *
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., Men's Haircut, Consultation, etc."
                                value={serviceForm.name}
                                onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
                                className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Description
                            </label>
                            <textarea
                                rows={3}
                                placeholder="Describe what this service includes..."
                                value={serviceForm.description}
                                onChange={(e) => setServiceForm(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm resize-none"
                            />
                        </div>

                        {/* Price */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Price (Optional)
                            </label>
                            <div className="flex space-x-2">
                                {/* Currency Dropdown */}
                                <select
                                    value={serviceForm.currency}
                                    onChange={(e) => setServiceForm(prev => ({ ...prev, currency: e.target.value }))}
                                    className="bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm min-w-[100px]"
                                >
                                    {currencyOptions.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                                {/* Price Input */}
                                <div className="relative flex-1">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                                        {currencyOptions.find(c => c.value === serviceForm.currency)?.symbol || '$'}
                                    </span>
                                    <input
                                        type="text"
                                        placeholder="0.00"
                                        value={serviceForm.price}
                                        onChange={(e) => setServiceForm(prev => ({ ...prev, price: e.target.value }))}
                                        className="w-full bg-gray-800 text-white pl-8 pr-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Picture Section */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Service Picture
                            </label>
                            <div className="space-y-3">
                                {/* Current Image Preview */}
                                {(serviceForm.image || serviceForm.imageUrl) && (
                                    <div className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-700">
                                        <img
                                            src={serviceForm.image || serviceForm.imageUrl}
                                            alt="Service preview"
                                            className="w-full h-full object-cover"
                                            onLoad={(e) => {
                                                console.log('✅ Service image loaded successfully:', e.target.src);
                                            }}
                                            onError={(e) => {
                                                console.error('❌ Service image failed to load:', e.target.src);
                                                e.target.style.display = 'none';
                                            }}
                                        />
                                        <button
                                            onClick={() => {
                                                console.log('🗑️ Removing service image');
                                                setServiceForm(prev => ({ ...prev, image: '', imageUrl: '' }));
                                            }}
                                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                                        >
                                            <Icon name="close" size={12} />
                                        </button>
                                    </div>
                                )}

                                {/* Upload Options */}
                                <div className="flex flex-col space-y-2">
                                    <button
                                        type="button"
                                        onClick={handleUploadImage}
                                        className="flex items-center justify-center space-x-2 px-4 py-2 border border-gray-700 rounded-lg hover:bg-gray-800 transition-colors text-gray-300"
                                    >
                                        <Icon name="upload" size={16} />
                                        <span className="text-sm">Upload Image</span>
                                    </button>

                                    <div className="relative">
                                        <div className="absolute inset-0 flex items-center">
                                            <div className="w-full border-t border-gray-700" />
                                        </div>
                                        <div className="relative flex justify-center text-sm">
                                            <span className="px-2 bg-gray-900 text-gray-500">or</span>
                                        </div>
                                    </div>

                                    <input
                                        type="url"
                                        placeholder="Enter image URL"
                                        value={serviceForm.imageUrl}
                                        onChange={(e) => setServiceForm(prev => ({ ...prev, imageUrl: e.target.value }))}
                                        className="w-full bg-gray-800 text-white px-3 py-2 rounded-lg border border-gray-700 focus:border-blue-500 focus:outline-none text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Duration */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                                Duration *
                            </label>
                            <div className="space-y-2">
                                {durationOptions.map((option) => (
                                    <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="duration"
                                            value={option.value}
                                            checked={serviceForm.duration === option.value}
                                            onChange={(e) => setServiceForm(prev => ({ ...prev, duration: e.target.value }))}
                                            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2"
                                        />
                                        <span className="text-sm text-gray-300">{option.label}</span>
                                    </label>
                                ))}
                            </div>

                            {/* Custom Duration Input */}
                            {serviceForm.duration === 'custom' && (
                                <div className="mt-3">
                                    <div className="flex items-center space-x-2">
                                        <input
                                            type="number"
                                            placeholder="Enter duration"
                                            value={serviceForm.customDuration}
                                            onChange={(e) => setServiceForm(prev => ({ ...prev, customDuration: e.target.value }))}
                                            className="flex-1 bg-gray-800 text-white px-3 py-2 border border-gray-700 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                                            min="1"
                                            max="480"
                                        />
                                        <span className="text-sm text-gray-400">minutes</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Form Footer */}
                <div className="flex-shrink-0 px-4 py-4 border-t border-gray-700 bg-gray-800 mt-auto">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => {
                                setShowAddServicePanel(false);
                                resetServiceForm();
                            }}
                            className="px-6 py-2.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-all duration-200 font-medium border border-gray-600 hover:border-gray-500 min-w-[100px]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveService}
                            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 font-medium shadow-lg hover:shadow-xl min-w-[140px]"
                        >
                            {editingServiceId ? 'Update Service' : 'Add Service'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppointmentServices;
